import { useLayoutEffect, useRef, useState, type MutableRefObject } from "react";
import { BookmarkPlus, History, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { fill } from "@/lib/i18n/fill";
import { friendlyIssueError } from "@/domains/pm/utils/issueErrors";
import { formatDateOnly } from "@/domains/pm/utils/protocol";
import type { ScheduleSnapshot } from "@/domains/pm/types";
import {
  deleteScheduleSnapshot,
  renameScheduleSnapshot,
  saveScheduleSnapshot,
  SnapshotDeleteRefusedError,
  SnapshotRenameUnavailableError,
} from "@/stores/pm/repository";
import { useScheduleSnapshots } from "./useScheduleSnapshots";
import { snapshotDay, snapshotName } from "./snapshotName";

/**
 * Save a snapshot, and browse the ones already saved.
 *
 * Saving is blocked while the Gantt has unconfirmed changes: a snapshot
 * records the schedule, and one taken over a half-applied arrangement would
 * record something that was never true.
 */
export function SnapshotControls({
  buildingProjectId,
  hasUnsavedChanges,
  canManage,
  onOpenSnapshot,
}: {
  buildingProjectId: number;
  hasUnsavedChanges: boolean;
  /** MANAGE_PM on the project: renaming and deleting are refused without it. */
  canManage: boolean;
  onOpenSnapshot: (snapshot: ScheduleSnapshot) => void;
}) {
  const { t, language } = useLanguage();
  const copy = t.pm.issues.gantt;
  const { data: snapshots = [] } = useScheduleSnapshots(buildingProjectId);
  const [saveOpen, setSaveOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [label, setLabel] = useState("");
  const [saving, setSaving] = useState(false);
  const [renamingId, setRenamingId] = useState<number | null>(null);
  // Read after an await, where the state in the closure is the stale one.
  // Synced in a layout effect, never during render: a render that is thrown
  // away (StrictMode, concurrent) must not leave its value behind.
  const renamingNow = useRef(renamingId);
  useLayoutEffect(() => {
    renamingNow.current = renamingId;
  }, [renamingId]);
  // Snapshots whose delete is on its way: a second click would match nothing
  // and report a refusal for a delete that worked.
  // A rename whose save is on its way: no other rename opens until it lands,
  // or a refusal would come back with no row left holding the typed name.
  const [renameSaving, setRenameSaving] = useState(false);
  const [deletingIds, setDeletingIds] = useState<ReadonlySet<number>>(() => new Set());
  // The open rename, so closing the popover can save it — the field is
  // unmounted with the popover, and a removed node delivers no blur in Chrome —
  // and Escape can abandon it wherever focus is.
  const openRename = useRef<RenameHandle | null>(null);

  /** Resolves whether the history actually closed. */
  const changeHistoryOpen = async (open: boolean): Promise<boolean> => {
    if (!open) {
      // Close once the edit is saved. A refused save keeps the history open on
      // that row, still holding what was typed, beside its toast; Escape
      // abandons it from there. A rename that cannot exist yet is not held.
      const saving = renamingId;
      if (openRename.current && (await openRename.current.commit()) === "refused") return false;
      // The popover stays interactive during that round trip. A rename opened
      // on another row meanwhile supersedes this close: closing now would
      // drop it unsaved.
      if (saving !== null && renamingNow.current !== null && renamingNow.current !== saving) {
        return false;
      }
      // Reopening the history shows the list, not the row left mid-rename.
      setRenamingId(null);
    }
    setHistoryOpen(open);
    return true;
  };

  const save = async () => {
    setSaving(true);
    try {
      await saveScheduleSnapshot(buildingProjectId, label.trim() || null);
      setLabel("");
      setSaveOpen(false);
    } catch (e) {
      toast.error(friendlyIssueError(e, t));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Button
        variant="ghost"
        size="control"
        disabled={hasUnsavedChanges}
        title={hasUnsavedChanges ? copy.snapshotBlockedByChanges : copy.saveSnapshot}
        onClick={() => setSaveOpen(true)}
        aria-label={copy.saveSnapshot}
      >
        {/* Icon-only, like History beside it: both live in the axis row's
            corner cell, which is only as wide as the issue-title column. */}
        <BookmarkPlus className="h-3.5 w-3.5" />
      </Button>

      <Popover open={historyOpen} onOpenChange={(open) => void changeHistoryOpen(open)}>
        <PopoverTrigger asChild>
          <Button variant="ghost" size="control" aria-label={copy.snapshotHistory}>
            <History className="h-3.5 w-3.5" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-80 p-1"
          align="end"
          // Radix hears Escape on the document before the field does: while a
          // row is being renamed, Escape abandons the rename — wherever focus
          // is, so a rename whose save keeps failing can always be left.
          // While its save is on the way a rename cannot be abandoned, so
          // Escape is left to close the history, which waits for that save.
          onEscapeKeyDown={(e) => {
            if (openRename.current?.abandon()) e.preventDefault();
          }}
        >
          {snapshots.length === 0 ? (
            <p className="px-2 py-3 text-center text-xs text-muted-foreground">
              {copy.snapshotEmpty}
            </p>
          ) : (
            <div className="max-h-80 overflow-y-auto">
              {snapshots.map((snapshot) =>
                renamingId === snapshot.id ? (
                  <SnapshotRenameRow
                    key={snapshot.id}
                    snapshot={snapshot}
                    buildingProjectId={buildingProjectId}
                    handleRef={openRename}
                    onSavingChange={setRenameSaving}
                    // A save resolving late must not close a rename the user
                    // has since opened on another row.
                    onDone={() => setRenamingId((id) => (id === snapshot.id ? null : id))}
                  />
                ) : (
                  <div key={snapshot.id} className="group flex items-center rounded hover:bg-muted">
                    <button
                      type="button"
                      onClick={async () => {
                        // Not over a rename that could not be saved.
                        if (await changeHistoryOpen(false)) onOpenSnapshot(snapshot);
                      }}
                      className="min-w-0 flex-1 px-2 py-1.5 text-left"
                    >
                      <span className="block truncate text-xs font-medium">
                        {snapshotName(snapshot, language)}
                      </span>
                      <span className="block truncate text-[10px] text-muted-foreground">
                        {snapshotDay(snapshot, language)} ·{" "}
                        {fill(copy.snapshotMeta, {
                          issues: String(snapshot.issue_count),
                          deps: String(snapshot.dependency_count),
                        })}
                      </span>
                    </button>
                    {canManage && (
                      <>
                        <button
                          type="button"
                          aria-label={`${copy.snapshotRename} ${snapshotName(snapshot, language)}`}
                          title={copy.snapshotRename}
                          disabled={renameSaving}
                          onClick={() => setRenamingId(snapshot.id)}
                          className={ROW_ACTION_CLASS}
                        >
                          <Pencil className="h-3 w-3" />
                        </button>
                        <button
                          type="button"
                          aria-label={`${t.common.delete} ${snapshotName(snapshot, language)}`}
                          disabled={deletingIds.has(snapshot.id)}
                          onClick={async () => {
                            if (deletingIds.has(snapshot.id)) return;
                            setDeletingIds((ids) => new Set(ids).add(snapshot.id));
                            try {
                              await deleteScheduleSnapshot(snapshot.id, buildingProjectId);
                            } catch (e) {
                              toast.error(
                                e instanceof SnapshotDeleteRefusedError
                                  ? copy.snapshotDeleteRefused
                                  : friendlyIssueError(e, t)
                              );
                            } finally {
                              setDeletingIds((ids) => {
                                const next = new Set(ids);
                                next.delete(snapshot.id);
                                return next;
                              });
                            }
                          }}
                          className={ROW_ACTION_CLASS}
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </>
                    )}
                  </div>
                )
              )}
            </div>
          )}
        </PopoverContent>
      </Popover>

      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>{copy.snapshotSaveTitle}</DialogTitle>
            <DialogDescription>{copy.snapshotHistory}</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="snapshot-label">{copy.snapshotLabel}</Label>
            <Input
              id="snapshot-label"
              value={label}
              autoFocus
              maxLength={120}
              placeholder={copy.snapshotLabelPlaceholder}
              onChange={(e) => setLabel(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  void save();
                }
              }}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveOpen(false)} disabled={saving}>
              {t.common.cancel}
            </Button>
            <Button onClick={() => void save()} disabled={saving}>
              {copy.snapshotSaveAction}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

const ROW_ACTION_CLASS =
  "shrink-0 rounded p-1.5 text-muted-foreground opacity-0 transition hover:bg-accent hover:text-foreground focus-visible:opacity-100 group-hover:opacity-100";

/** How a save ended: kept, refused (worth holding the row for), or impossible
 *  because the rename RPC is not deployed yet (nothing to hold it for). */
type RenameOutcome = "saved" | "refused" | "unavailable";

interface RenameHandle {
  commit: () => Promise<RenameOutcome>;
  /** Drops the edit; false (and nothing done) while its save is on the way. */
  abandon: () => boolean;
}

/**
 * A history row being renamed, in place. Enter or leaving the field saves,
 * Escape abandons; a blank name clears the label, so the row falls back to the
 * date it was taken. The field stays open until the save lands, and stays
 * open on a refusal, so a name is never shown that the database did not keep.
 */
function SnapshotRenameRow({
  snapshot,
  buildingProjectId,
  handleRef,
  onSavingChange,
  onDone,
}: {
  snapshot: ScheduleSnapshot;
  buildingProjectId: number;
  /** Set to this row's commit and abandon while it is open, for the popover. */
  handleRef: MutableRefObject<RenameHandle | null>;
  onSavingChange: (saving: boolean) => void;
  onDone: () => void;
}) {
  const { t } = useLanguage();
  const copy = t.pm.issues.gantt;
  const [value, setValue] = useState(snapshot.label ?? "");
  const [saving, setSaving] = useState(false);
  useLayoutEffect(() => {
    onSavingChange(saving);
    return () => onSavingChange(false);
  }, [saving, onSavingChange]);
  // Escape unmounts the field, which can still deliver a blur on its way out.
  const abandoned = useRef(false);
  // The save already on its way, if any. A ref, not the `saving` state: blur
  // and the popover closing can both ask within one render, and the second
  // must share the first's answer rather than send the rename again.
  const inFlight = useRef<Promise<RenameOutcome> | null>(null);

  const commit = (): Promise<RenameOutcome> => {
    if (abandoned.current) return Promise.resolve("saved");
    if (inFlight.current) return inFlight.current;
    const next = value.trim() || null;
    if (next === snapshot.label) {
      onDone();
      return Promise.resolve("saved");
    }
    setSaving(true);
    const save = (async (): Promise<RenameOutcome> => {
      try {
        await renameScheduleSnapshot(snapshot.id, buildingProjectId, next);
        onDone();
        return "saved";
      } catch (e) {
        const unavailable = e instanceof SnapshotRenameUnavailableError;
        // One toast per row: retrying by clicking away again replaces it
        // rather than stacking another.
        toast.error(unavailable ? copy.snapshotRenameUnavailable : friendlyIssueError(e, t), {
          id: `snapshot-rename-${snapshot.id}`,
        });
        setSaving(false);
        return unavailable ? "unavailable" : "refused";
      } finally {
        inFlight.current = null;
      }
    })();
    inFlight.current = save;
    return save;
  };

  // Not while a save is on its way: it cannot be called back, so escaping
  // would hide a rename that still lands — or a refusal with no row to retry.
  const abandon = (): boolean => {
    if (inFlight.current) return false;
    abandoned.current = true;
    onDone();
    return true;
  };

  // Every render, so the popover always commits what the field holds now.
  useLayoutEffect(() => {
    const own: RenameHandle = { commit, abandon };
    handleRef.current = own;
    return () => {
      if (handleRef.current === own) handleRef.current = null;
    };
  });

  return (
    <div className="px-1 py-1">
      <Input
        value={value}
        autoFocus
        maxLength={120}
        disabled={saving}
        aria-label={copy.snapshotRename}
        placeholder={copy.snapshotLabelPlaceholder}
        className="h-7 text-xs"
        onFocus={(e) => e.currentTarget.select()}
        onChange={(e) => setValue(e.target.value)}
        onBlur={() => void commit()}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            void commit();
          } else if (e.key === "Escape") {
            e.preventDefault();
            abandon();
          }
        }}
      />
    </div>
  );
}
